import { cn, getObjectKeys } from "@gettactic/helpers";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check } from "lucide-react";
import * as React from "react";
import {
  SelectItemIndicator,
  SelectItemText,
  useSelectContext
} from "@cmp-src/primitives/Select";

export interface SelectItemState {
  isSelected: boolean;
  value?: string;
  isValueItem?: boolean;
}

export const SelectItemContext = React.createContext<
  SelectItemState | undefined
>(undefined);

export const useSelectItemContext = () => {
  const context = React.useContext(SelectItemContext);
  if (context === undefined) {
    throw new Error(
      "useSelectItemContext must be used within a SelectItemContext.Provider"
    );
  }

  return context;
};

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    checkClassName?: string;
    selectItemTextProps?: React.ComponentPropsWithoutRef<typeof SelectItemText>;
    skipSelectItemText?: boolean;
  }
>(({ className, children, checkClassName, selectItemTextProps, skipSelectItemText, ...props }, ref) => {
  const { isMobile, value, onValueChange, valueItems, searchValue } =
    useSelectContext();

  const _className = cn(
    "relative gap-2 flex w-full cursor-default select-none items-center rounded-sm px-1 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    className
  );

  const isSelected = props.value === value;
  const itemContextValue = React.useContext(SelectItemContext);

  const content = (
    <SelectItemContext.Provider
      value={
        itemContextValue || {
          isSelected,
          value: props.value
        }
      }
    >
      {!itemContextValue?.isValueItem && (
        <span
          className={cn(
            "flex h-3.5 w-3.5 items-center justify-center",
            checkClassName ? checkClassName : ""
          )}
        >
          <SelectItemIndicator>
            <Check className="h-4 w-4" />
          </SelectItemIndicator>
        </span>
      )}

      {skipSelectItemText ? (
        <div className="w-full flex-1">
          {children}
        </div>
      ) : (
        <SelectItemText {...selectItemTextProps}>
          {children}
        </SelectItemText>
      )}
    </SelectItemContext.Provider>
  );

  if (isMobile) {
    if (searchValue) {
      const currentItem = getObjectKeys(valueItems).find(
        (itemValue) => itemValue === props.value
      ) as keyof typeof valueItems;

      if (!currentItem || !valueItems[currentItem]) {
        return null;
      }

      if (
        !valueItems[currentItem].text.find((text) =>
          text.toLowerCase().includes(searchValue.toLowerCase())
        )
      ) {
        return null;
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          _className,
          !itemContextValue?.isValueItem ? "py-2" : "",
          "md:pb-0"
        )}
        onClick={() => onValueChange?.(props.value)}
      >
        {content}
      </div>
    );
  }

  return (
    <SelectPrimitive.Item ref={ref} className={_className} {...props}>
      {content}
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;
